import { useUserInvoicesQuery } from "@/components/Dashboard/v2/hooks/useUserInvoicesLazyQuery";
import { ComponentCard } from "@/components/Dashboard/v2/ui";
import clsx from "clsx";
import { ChevronDown, Download } from "@pkg/icons";
import { useState } from "react";

const PaymentInvoice = () => {
  const [showInvoices, setShowInvoices] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [expandedInvoices, setExpandedInvoices] = useState<Set<number>>(
    new Set(),
  );

  // Query is enabled when shouldFetch is true
  const {
    data: invoices,
    isLoading,
    isError,
  } = useUserInvoicesQuery(shouldFetch);

  const handleToggleShow = () => {
    const newShowState = !showInvoices;
    setShowInvoices(newShowState);

    // Only trigger fetch when showing for the first time
    if (newShowState && !invoices) {
      setShouldFetch(true);
    }
  };

  const toggleInvoice = (index: number) => {
    setExpandedInvoices((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  return (
    <div className="app-container px-5">
      <ComponentCard className="">
        {/* <div className="border-edge-light  mx-auto w-full max-w-7xl rounded-lg bg-white px-5  py-6 text-nightRider sm:p-8 md:border"> */}
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-medium tracking-[3.6px]">INVOICES</h2>

          <button
            onClick={handleToggleShow}
            className="text-type-dark-grey flex items-center gap-2 text-xs font-medium"
          >
            {showInvoices ? "Hide" : "Show"}
            <ChevronDown
              className={clsx(
                "h-4 w-4 transition-transform duration-200",
                showInvoices && "rotate-180",
              )}
            />
          </button>
        </div>

        {/* Loading State */}
        {showInvoices && isLoading && (
          <div className="my-6 space-y-4">
            <div className="h-12 animate-pulse rounded-sm bg-gray-100" />
            <div className="hidden h-12 animate-pulse rounded-sm bg-gray-100 lg:block" />
            <div className="hidden h-12 animate-pulse rounded-sm bg-gray-100 md:block" />
          </div>
        )}

        {/* Error State */}
        {showInvoices && isError && (
          <div className="my-6 rounded-lg bg-red-50 p-4 text-center text-sm text-red-600">
            Failed to load invoices. Please try again.
          </div>
        )}

        {/* Desktop Table */}
        {showInvoices && invoices && invoices.length > 0 && !isLoading && (
          <div className="mt-4 hidden md:block">
            <table className="my-6 w-full divide-y-2 divide-gray-200 text-left text-sm font-semibold text-nightRider">
              <thead>
                <tr className="text-xs md:text-sm">
                  <th className="px-1">Date</th>
                  <th className="px-1">Amount</th>
                  {/* <th className="px-1">Type</th> */}
                  <th className="px-1">Status</th>
                  <th className="px-1">Download</th>
                </tr>
              </thead>
              <tbody className="divide-y-2">
                {invoices.map((invoice, key) => {
                  const formattedAmount = (invoice.amount_paid / 100).toFixed(
                    2,
                  );

                  return (
                    <tr className="text-xs md:text-base" key={key}>
                      <td className="font-light">
                        <div className="truncate px-1 py-2">{invoice.date}</div>
                      </td>
                      <td className="font-semibold">
                        <div className="px-1">
                          <span className="pr-1 uppercase">
                            {invoice.currency}
                          </span>
                          {formattedAmount}
                        </div>
                      </td>
                      {/* <td className="font-light">
                        <div className="truncate px-1">
                          {invoice.invoice_type || "-"}
                        </div>
                      </td> */}
                      <td className="px-1">
                        <span
                          className={clsx(
                            "inline-flex rounded-md px-3 py-1 text-[10px] font-semibold text-white uppercase md:text-xs",
                            invoice.status === "paid"
                              ? "bg-lochinvar"
                              : "bg-[#D77073]",
                          )}
                        >
                          {invoice.status}
                        </span>
                      </td>
                      <td className="px-1">
                        <a
                          href={invoice.download_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-6 inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          <Download className="w-4" />
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Mobile Cards */}
        {showInvoices && invoices && invoices.length > 0 && !isLoading && (
          <div className="mt-4 space-y-3 md:hidden">
            {invoices.map((invoice, key) => {
              const formattedAmount = (invoice.amount_paid / 100).toFixed(2);
              const isExpanded = expandedInvoices.has(key);

              return (
                <div
                  key={key}
                  className="overflow-hidden rounded-lg border border-gray-200"
                >
                  {/* Header - Always Visible */}
                  <button
                    onClick={() => toggleInvoice(key)}
                    className="flex w-full items-center justify-between p-4 transition-colors hover:bg-gray-50"
                  >
                    <div className="flex flex-1 items-center gap-3">
                      <div className="text-left">
                        <p className="text-sm font-semibold">{invoice.date}</p>
                        <p className="text-xs text-gray-500">
                          <span className="uppercase">{invoice.currency}</span>{" "}
                          {formattedAmount}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span
                        className={clsx(
                          "rounded-md px-2 py-1 text-[10px] font-semibold text-white uppercase",
                          invoice.status === "paid"
                            ? "bg-lochinvar"
                            : "bg-[#D77073]",
                        )}
                      >
                        {invoice.status}
                      </span>
                      {/* <ChevronDown
                        className={clsx(
                          "h-5 w-5 text-gray-500 transition-transform duration-200",
                          isExpanded && "rotate-180",
                        )}
                      />
                      */}
                      <button className="ml-1 flex items-center">
                        <a
                          href={invoice.download_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          <Download className="w-4" />
                        </a>
                      </button>
                    </div>
                  </button>

                  {/* Expandable Content */}
                  {/* <button
                    className={clsx(
                      "overflow-hidden transition-all duration-300 ease-in-out",
                      isExpanded ? "max-h-96 opacity-100" : "max-h-0 opacity-0",
                    )}
                  >
                    <a
                      href={invoice.download_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      Download Invoice
                    </a>
                  </button> */}
                </div>
              );
            })}
          </div>
        )}
        {/* </div> */}
      </ComponentCard>
    </div>
  );
};

export default PaymentInvoice;
